import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import Link from '@mui/material/Link';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { listPlannedLessons } from '@platform/client';
import { MAX_SELECTED } from '@platform/schema/chat';
import {
  MAX_LESSON_PAGE,
  plannedLessonListSchema,
  plannedLessonStatusSchema,
} from '@platform/schema/curriculum';
import {
  keepPreviousData,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useState } from 'react';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';

import { describeApiError } from '../api-error';
import { explainFailure } from '../authoring/explain';
import { PublishDialog } from '../authoring/PublishDialog';
import { counted } from '../library/counted';
import { FilterSelect } from '../library/FilterSelect';
import { Pager } from '../library/Pager';
import { jobQuery } from '../library/sources';
import { PageHeader, SCREEN_GAP } from '../routes/PageHeader';

import {
  isRunning,
  REBUILD_LABEL,
  rebuildPhase,
  STATUS_LABEL,
  statusLabel,
  STORY_CHANGED,
} from './actions';
import { AddDialog } from './AddDialogs';
import { useAskAi } from './AskAiProvider';
import { BuildDialog, CreateInAppDialog, VerifyDialog } from './BuildDialogs';
import { BuildProgress, POLL_MS } from './BuildProgress';
import { CurriculumChat, panelWidth, ScopeSwitch } from './CurriculumChat';
import { ExportMenu } from './ExportMenu';
import { ImportDialog } from './ImportDialog';
import { LessonDrawer, PANEL_GAP } from './LessonDrawer';
import {
  ModuleStoryDialog,
  plannedCoursesQuery,
  plannedLessonsWhere,
} from './ModuleStory';
import { RebuildDialog, rebuildTargetOf } from './RebuildDialog';
import { LessonRenameDialog, ModuleRenameDialog } from './RenameDialog';
import { RowActions } from './RowActions';
import { ALL, countOf, isPicked, NONE, pick, toggle } from './selection';
import {
  DEFAULT_VIEW,
  filtersOf,
  PAGE_SIZES,
  readView,
  sameFilters,
  writeView,
} from './view';

import type { Adding } from './AddDialogs';
import type { ScopeKind, ScopeOption } from './CurriculumChat';
import type { RebuildRow, RebuildTarget } from './RebuildDialog';
import type { Selection } from './selection';
import type { View } from './view';
import type { PublishTarget } from '../authoring/PublishDialog';
import type { PlanningScope } from '@platform/schema/chat';
import type { PlannedLesson } from '@platform/schema/curriculum';
import type { ReactNode } from 'react';

/**
 * The Curriculum tab — the plan of every lesson the CSV names, before and after it is
 * built. A planned lesson is not a `Lesson`: adding one here changes nothing under
 * Lessons until it is built (CURR-22).
 */

/** The Lesson IDs a selection names, in table order when it is every match. */
async function selectedIds(
  selection: Selection,
  view: View,
): Promise<string[]> {
  if (!selection.all) return [...selection.ids];
  const ids = await idsWhere({
    ...filtersOf(view),
    sort: view.sort,
    order: view.order,
  });
  return ids.filter((id) => !selection.except.has(id));
}

async function idsWhere(
  query: Parameters<typeof plannedLessonsWhere>[0],
): Promise<string[]> {
  return (await plannedLessonsWhere(query)).map((row) => row.lessonId);
}

/** The Lesson IDs the Ask AI panel's scope names, so a build or verify acts on them. */
function scopeIds(scope: PlanningScope): Promise<string[]> {
  switch (scope.kind) {
    case 'lesson':
      return Promise.resolve([scope.lessonId]);
    case 'selected':
      return Promise.resolve([...scope.lessonIds]);
    case 'filter':
      return idsWhere(scope.filter);
    case 'curriculum':
      return idsWhere({});
  }
}

const SORTS: { value: string; label: string }[] = [
  { value: 'position:asc', label: 'Curriculum order' },
  { value: 'lessonId:asc', label: 'Lesson ID — A–Z' },
  { value: 'course:asc', label: 'Course — A–Z' },
  { value: 'module:asc', label: 'Module — A–Z' },
  { value: 'name:asc', label: 'Lesson name — A–Z' },
  { value: 'name:desc', label: 'Lesson name — Z–A' },
  { value: 'status:asc', label: 'Status' },
  { value: 'publishedAt:desc', label: 'Published — newest first' },
  { value: 'updatedAt:desc', label: 'Last updated — newest first' },
];

const PREVIEW_WIDTH = 260;
const NAME_WIDTH = 220;
const FILTER_WIDTH = 200;
const SEARCH_WIDTH = 260;
/** Enough of a writer brief to recognise it; the drawer holds the whole of it. */
const PREVIEW_CHARS = 400;

export function Curriculum() {
  const [params, setParams] = useSearchParams();
  const view = readView(params);
  const [selection, setSelection] = useState<Selection>(NONE);
  const [cleared, setCleared] = useState(false);
  const [importing, setImporting] = useState(false);
  const [renaming, setRenaming] = useState<'module' | 'lesson' | null>(null);
  const [adding, setAdding] = useState<Adding | null>(null);
  const [addMenu, setAddMenu] = useState<HTMLElement | null>(null);
  const [added, setAdded] = useState<{
    message: string;
    lessonId: string | undefined;
  } | null>(null);
  const [open, setOpen] = useState<string | null>(null);
  // `?story=<module id>` is Library's way back here to edit a module's story.
  const [storyOf, setStoryOf] = useState(params.get('story'));
  // The lesson last opened in the drawer: the AI panel's "Current lesson".
  const ai = useAskAi();
  const [current, setCurrent] = useState<string | null>(
    ai.scope.kind === 'lesson' ? ai.scope.lessonId : null,
  );
  const [building, setBuilding] = useState<{
    ids: readonly string[];
    verify: boolean;
  } | null>(null);
  const [verifying, setVerifying] = useState<readonly string[] | null>(null);
  const [creating, setCreating] = useState<readonly string[] | null>(null);
  const [resolving, setResolving] = useState<string | null>(null);
  const [publishing, setPublishing] = useState<PublishTarget | null>(null);
  const [rebuilding, setRebuilding] = useState<RebuildTarget | null>(null);
  // A published lesson's rebuild, waiting on Move to draft — the API refuses it before.
  const [parked, setParked] = useState<RebuildTarget | null>(null);
  const queries = useQueryClient();
  const rebuild = (row: RebuildRow): void => {
    if (row.builtLessonId === null) return;
    const target = rebuildTargetOf(row, row.builtLessonId);
    if (!row.published) {
      setRebuilding(target);
      return;
    }
    setParked(target);
    setPublishing({
      lesson: target.builtLessonId,
      action: 'unpublish',
      name: `${target.lessonId} · ${target.name}`,
      place: target.place,
      why: 'This lesson is published. Move it to draft before rebuilding.',
    });
  };

  const courses = useQuery(plannedCoursesQuery);
  const page = useQuery({
    queryKey: ['curriculum', 'lessons', writeView(view).toString()],
    placeholderData: keepPreviousData,
    refetchInterval: (query) =>
      query.state.data?.lessons.some(
        (row) => isRunning(row.status) || row.rebuilding,
      ) === true
        ? POLL_MS
        : false,
    // Parsed, not cast: the SDK types a nullable field as `unknown`.
    queryFn: async () =>
      plannedLessonListSchema.parse(
        (
          await listPlannedLessons({
            query: {
              ...filtersOf(view),
              sort: view.sort,
              order: view.order,
              limit: view.limit,
              offset: view.offset,
            },
            throwOnError: true,
          })
        ).data,
      ),
  });

  // What a reload forgets of `ai.started`, recovered from the lessons still being built.
  const running = useQuery({
    queryKey: ['curriculum', 'running'],
    refetchInterval: (query) =>
      (query.state.data?.length ?? 0) > 0 ? POLL_MS : false,
    queryFn: async () => {
      const lists = await Promise.all(
        (['QUEUED', 'BUILDING'] as const).map(
          async (status) =>
            plannedLessonListSchema.parse(
              (
                await listPlannedLessons({
                  query: { status, limit: MAX_LESSON_PAGE },
                  throwOnError: true,
                })
              ).data,
            ).lessons,
        ),
      );
      return lists.flat().flatMap((row) => row.buildJobId ?? []);
    },
  });

  const rows = page.data?.lessons ?? [];
  const total = page.data?.total ?? 0;
  const planned = (courses.data ?? []).reduce(
    (sum, course) =>
      sum + course.modules.reduce((n, module) => n + module.lessons, 0),
    0,
  );
  const picked = countOf(selection, total);
  const jobs = [...new Set([...ai.started, ...(running.data ?? [])])];

  const withIds = (
    ids: Promise<string[]>,
    then: (ids: string[]) => void,
  ): void => {
    setResolving(null);
    ids.then(then, (error: unknown) => {
      setResolving(describeApiError(error));
    });
  };
  const withSelected = (then: (ids: string[]) => void): void => {
    withIds(selectedIds(selection, view), then);
  };
  const buildAll = (verify: boolean) => (ids: string[]) => {
    setBuilding({ ids, verify });
  };

  // A selection names rows of one filter. When the filter changes, an action could run
  // on rows nobody can see any more, so it is cleared and the admin is told.
  const show = (next: View): void => {
    const refiltered = !sameFilters(view, next);
    if (refiltered) {
      if (picked > 0) setCleared(true);
      setSelection(NONE);
    }
    setParams(writeView(refiltered ? { ...next, offset: 0 } : next), {
      replace: true,
    });
  };
  const choose = (next: Selection): void => {
    setCleared(false);
    setSelection(next);
  };

  const course = (courses.data ?? []).find((one) => one.id === view.courseId);
  const moduleOptions = (courses.data ?? []).flatMap((one) =>
    course === undefined || one.id === course.id
      ? one.modules.map((module) => ({
          value: module.id,
          label:
            course === undefined ? `${one.name} › ${module.name}` : module.name,
        }))
      : [],
  );

  // A row click follows the AI panel to its lesson only while nothing is under way there;
  // otherwise it offers the switch, and the scope changes on the admin's answer.
  const openLesson = (lessonId: string): void => {
    setOpen(lessonId);
    setCurrent(lessonId);
    if (ai.scope.kind !== 'lesson' || ai.scope.lessonId === lessonId) return;
    if (ai.asking || ai.said.length > 0) ai.propose(lessonId);
    else ai.setScope({ kind: 'lesson', lessonId });
  };

  const scopes: ScopeOption[] = [
    {
      kind: 'lesson',
      label:
        current === null ? 'Current lesson' : `Current lesson (${current})`,
      unavailable: current === null ? 'Open a lesson’s details first.' : null,
    },
    {
      kind: 'selected',
      label: `Selected lessons (${picked.toLocaleString()})`,
      unavailable:
        picked === 0
          ? 'Select lessons in the table first.'
          : picked > MAX_SELECTED
            ? `Select at most ${MAX_SELECTED.toLocaleString()}, or ask about the filtered lessons.`
            : null,
    },
    {
      kind: 'filter',
      label: `Filtered lessons (${total.toLocaleString()})`,
      unavailable: total === 0 ? 'No lesson matches the filters.' : null,
    },
    {
      kind: 'curriculum',
      label: `Entire curriculum (${planned.toLocaleString()})`,
      unavailable: planned === 0 ? 'The plan is empty.' : null,
    },
  ];

  // Read from the table when the admin picks the scope, and kept until they pick again.
  const pickScope = (kind: ScopeKind): void => {
    if (kind === 'selected')
      withSelected((lessonIds) => {
        ai.setScope({ kind, lessonIds });
      });
    else if (kind === 'lesson')
      ai.setScope(
        current === null ? { kind: 'curriculum' } : { kind, lessonId: current },
      );
    else if (kind === 'filter') ai.setScope({ kind, filter: filtersOf(view) });
    else ai.setScope({ kind });
  };

  const pageIds = rows.map((row) => row.lessonId);
  const wholePage =
    pageIds.length > 0 && pageIds.every((id) => isPicked(selection, id));
  const somePage = pageIds.some((id) => isPicked(selection, id));

  return (
    <Stack spacing={SCREEN_GAP} sx={{ height: '100%' }}>
      <PageHeader
        title="Curriculum"
        subtitle={`${counted(planned, 'planned lesson')}. Planned lessons are not in Lessons until they are built.`}
        action={
          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              aria-pressed={ai.mode !== 'closed'}
              onClick={() => {
                ai.setMode(ai.mode === 'open' ? 'closed' : 'open');
              }}
            >
              Ask AI
            </Button>
            <Button
              variant="contained"
              onClick={() => {
                setImporting(true);
              }}
            >
              Import CSV
            </Button>
            <Button
              variant="outlined"
              aria-haspopup="menu"
              onClick={(event) => {
                setAddMenu(event.currentTarget);
              }}
            >
              Add
            </Button>
            <Menu
              anchorEl={addMenu}
              open={addMenu !== null}
              onClose={() => {
                setAddMenu(null);
              }}
            >
              {(['course', 'module', 'lesson'] as const).map((kind) => (
                <MenuItem
                  key={kind}
                  onClick={() => {
                    setAddMenu(null);
                    setAdding(kind);
                  }}
                >
                  Planned {kind}
                </MenuItem>
              ))}
            </Menu>
            <Button
              variant="outlined"
              onClick={() => {
                setRenaming('module');
              }}
            >
              Duplicate module names
            </Button>
            <Button
              variant="outlined"
              onClick={() => {
                setRenaming('lesson');
              }}
            >
              Duplicate lesson titles
            </Button>
            <ExportMenu
              view={view}
              selection={selection}
              counts={{
                page: rows.length,
                filtered: total,
                selected: picked,
                all: planned,
              }}
            />
          </Stack>
        }
      />
      <Stack
        direction="row"
        spacing={PANEL_GAP}
        sx={{ flex: 1, minHeight: 0, alignItems: 'flex-start' }}
      >
        <Stack
          spacing={SCREEN_GAP}
          sx={{ flex: 1, minWidth: 0, alignSelf: 'stretch', overflowY: 'auto' }}
        >
          <Stack
            direction="row"
            spacing={1}
            sx={{ flexWrap: 'wrap', rowGap: 1 }}
          >
            <TextField
              size="small"
              label="Search"
              placeholder="ID, name, description, brief"
              value={view.q}
              onChange={(event) => {
                show({ ...view, q: event.target.value });
              }}
              sx={{ minWidth: SEARCH_WIDTH }}
            />
            <FilterSelect
              label="Course"
              value={view.courseId}
              width={FILTER_WIDTH}
              options={(courses.data ?? []).map((one) => ({
                value: one.id,
                label: one.name,
              }))}
              onChange={(courseId) => {
                const keep = (courses.data ?? [])
                  .find((one) => one.id === courseId)
                  ?.modules.some((module) => module.id === view.moduleId);
                show({
                  ...view,
                  courseId,
                  moduleId: keep === true ? view.moduleId : '',
                });
              }}
            />
            <FilterSelect
              label="Module"
              value={view.moduleId}
              width={FILTER_WIDTH}
              options={moduleOptions}
              onChange={(moduleId) => {
                show({ ...view, moduleId });
              }}
            />
            <FilterSelect
              label="Status"
              value={view.status}
              width={FILTER_WIDTH}
              options={plannedLessonStatusSchema.options.map((value) => ({
                value,
                label: STATUS_LABEL[value],
              }))}
              onChange={(status) => {
                show({ ...view, status: status as View['status'] });
              }}
            />
            <FilterSelect
              label="Published"
              value={view.published}
              width={FILTER_WIDTH}
              options={[
                { value: 'yes', label: 'Yes' },
                { value: 'no', label: 'No' },
              ]}
              onChange={(published) => {
                show({ ...view, published: published as View['published'] });
              }}
            />
            <TextField
              select
              size="small"
              label="Sort"
              value={`${view.sort}:${view.order}`}
              onChange={(event) => {
                const [sort, order] = event.target.value.split(':');
                show({
                  ...view,
                  sort: sort as View['sort'],
                  order: order as View['order'],
                });
              }}
              sx={{ minWidth: FILTER_WIDTH }}
            >
              {SORTS.map((one) => (
                <MenuItem key={one.value} value={one.value}>
                  {one.label}
                </MenuItem>
              ))}
            </TextField>
            <Button
              onClick={() => {
                show(DEFAULT_VIEW);
              }}
            >
              Reset
            </Button>
          </Stack>

          {added !== null && (
            <Alert
              severity="success"
              onClose={() => {
                setAdded(null);
              }}
              action={
                added.lessonId === undefined ? undefined : (
                  <Button
                    size="small"
                    color="inherit"
                    onClick={() => {
                      show({ ...DEFAULT_VIEW, q: added.lessonId ?? '' });
                    }}
                  >
                    Show it
                  </Button>
                )
              }
            >
              {added.message}
            </Alert>
          )}
          {cleared && (
            <Alert
              severity="info"
              onClose={() => {
                setCleared(false);
              }}
            >
              The filters changed, so the selection was cleared.
            </Alert>
          )}

          <SelectionBar
            selection={selection}
            total={total}
            onPage={rows.length}
            wholePage={wholePage}
            onChoose={choose}
            actions={
              <>
                <Button
                  size="small"
                  variant="contained"
                  onClick={() => {
                    withSelected(buildAll(false));
                  }}
                >
                  Build selected lessons
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => {
                    withSelected(buildAll(true));
                  }}
                >
                  Build and verify
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => {
                    withSelected(setVerifying);
                  }}
                >
                  Verify
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => {
                    withSelected(setCreating);
                  }}
                >
                  Create their courses and modules in app
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => {
                    withSelected((lessonIds) => {
                      ai.setScope({ kind: 'selected', lessonIds });
                      ai.setMode('open');
                      ai.instruct();
                    });
                  }}
                >
                  AI edit
                </Button>
              </>
            }
          />
          {resolving !== null && <Alert severity="error">{resolving}</Alert>}

          <BuildProgress jobIds={jobs} />

          {page.isError && (
            <Alert severity="error">{describeApiError(page.error)}</Alert>
          )}

          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table size="small" aria-label="Planned lessons">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={wholePage}
                      indeterminate={somePage && !wholePage}
                      disabled={rows.length === 0}
                      slotProps={{
                        input: {
                          'aria-label': `Select the ${String(rows.length)} lessons on this page`,
                        },
                      }}
                      onChange={() => {
                        choose(pick(selection, pageIds, !wholePage));
                      }}
                    />
                  </TableCell>
                  <TableCell>Lesson ID</TableCell>
                  <TableCell>Course</TableCell>
                  <TableCell>Module</TableCell>
                  <TableCell>Lesson name</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Actions</TableCell>
                  <TableCell>Published</TableCell>
                  <TableCell>Published on</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Writer brief</TableCell>
                  <TableCell>Sources</TableCell>
                  <TableCell>Details</TableCell>
                  <TableCell>Built lesson</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row) => (
                  <TableRow
                    key={row.id}
                    hover
                    selected={isPicked(selection, row.lessonId)}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={isPicked(selection, row.lessonId)}
                        slotProps={{
                          input: { 'aria-label': `Select ${row.lessonId}` },
                        }}
                        onChange={() => {
                          choose(toggle(selection, row.lessonId));
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {row.lessonId}
                    </TableCell>
                    <Cut text={row.course} width={NAME_WIDTH} />
                    <Cut
                      text={row.module}
                      width={NAME_WIDTH}
                      action={{
                        label: `${row.module} — module story`,
                        onClick: () => {
                          setStoryOf(row.moduleId);
                        },
                      }}
                    />
                    <Cut text={row.name} width={NAME_WIDTH} />
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      <RowStatus row={row} />
                      {row.buildError !== null && (
                        <Tooltip describeChild title={row.buildError}>
                          <Typography
                            variant="body2"
                            color="error"
                            noWrap
                            sx={{ maxWidth: NAME_WIDTH }}
                          >
                            {explainFailure(row.buildError)?.title ??
                              row.buildError}
                          </Typography>
                        </Tooltip>
                      )}
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      <RowActions
                        row={row}
                        onBuild={(lessonId) => {
                          setBuilding({ ids: [lessonId], verify: false });
                        }}
                        onPublish={setPublishing}
                        onRebuild={rebuild}
                      />
                    </TableCell>
                    <TableCell>{row.published ? 'Yes' : 'No'}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {row.publishedAt === null
                        ? '—'
                        : new Date(row.publishedAt).toLocaleDateString()}
                    </TableCell>
                    <Cut text={row.description} width={PREVIEW_WIDTH} />
                    <Cut text={row.writerBrief} width={PREVIEW_WIDTH} />
                    <TableCell>
                      <Sources row={row} />
                    </TableCell>
                    <TableCell>
                      <Button
                        size="small"
                        onClick={() => {
                          openLesson(row.lessonId);
                        }}
                      >
                        Open details
                      </Button>
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {row.builtLessonId === null ? (
                        '—'
                      ) : (
                        <Link
                          component={RouterLink}
                          to={`/authoring/lessons/${row.builtLessonId}`}
                        >
                          Open lesson
                        </Link>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {page.isSuccess && rows.length === 0 && (
            <Typography color="text.secondary">
              {planned === 0
                ? 'The plan is empty. Import the curriculum CSV to start.'
                : 'No planned lesson matches these filters.'}
            </Typography>
          )}

          <Stack direction="row" spacing={2} sx={{ alignItems: 'center' }}>
            <Pager
              offset={view.offset}
              total={total}
              pageSize={view.limit}
              onOffset={(offset) => {
                show({ ...view, offset });
              }}
            />
            <TextField
              select
              size="small"
              label="Rows per page"
              value={view.limit}
              onChange={(event) => {
                show({ ...view, limit: Number(event.target.value), offset: 0 });
              }}
              sx={{ minWidth: FILTER_WIDTH }}
            >
              {PAGE_SIZES.map((size) => (
                <MenuItem key={size} value={size}>
                  {size}
                </MenuItem>
              ))}
            </TextField>
            <Typography variant="body2" color="text.secondary">
              {counted(total, 'lesson')} match.
            </Typography>
          </Stack>

          <ImportDialog
            open={importing}
            onClose={() => {
              setImporting(false);
            }}
          />
          <ModuleStoryDialog
            moduleId={storyOf}
            onClose={() => {
              setStoryOf(null);
            }}
          />
          <ModuleRenameDialog
            open={renaming === 'module'}
            onClose={() => {
              setRenaming(null);
            }}
          />
          <LessonRenameDialog
            open={renaming === 'lesson'}
            onClose={() => {
              setRenaming(null);
            }}
          />
          <AddDialog
            adding={adding}
            courses={courses.data ?? []}
            onClose={() => {
              setAdding(null);
            }}
            onAdded={(message, lessonId) => {
              setAdded({ message, lessonId });
            }}
          />
          <BuildDialog
            ids={building?.ids ?? null}
            verify={building?.verify ?? false}
            onClose={() => {
              setBuilding(null);
            }}
            onBuilt={(jobId) => {
              ai.addStarted(jobId);
            }}
            onEditStory={(moduleId) => {
              setBuilding(null);
              setStoryOf(moduleId);
            }}
          />
          <VerifyDialog
            ids={verifying}
            onClose={() => {
              setVerifying(null);
            }}
            onStarted={(jobId) => {
              ai.addStarted(jobId);
            }}
          />
          <PublishDialog
            target={publishing}
            onClose={() => {
              setPublishing(null);
              setParked(null);
            }}
            onDone={async () => {
              await queries.invalidateQueries({ queryKey: ['curriculum'] });
              setRebuilding(parked);
            }}
          />
          <RebuildDialog
            key={rebuilding?.lessonId ?? ''}
            target={rebuilding}
            onClose={() => {
              setRebuilding(null);
            }}
            onStarted={(jobId) => {
              ai.addStarted(jobId);
            }}
          />
          <CreateInAppDialog
            ids={creating}
            onClose={() => {
              setCreating(null);
            }}
          />
          <LessonDrawer
            key={open}
            lessonId={open}
            courses={courses.data ?? []}
            beside={panelWidth(ai.mode)}
            notice={
              open !== null && ai.proposed === open ? (
                <ScopeSwitch lessonId={open} />
              ) : undefined
            }
            onClose={() => {
              setOpen(null);
            }}
            onBuild={(lessonId) => {
              setBuilding({ ids: [lessonId], verify: false });
            }}
            onRebuild={rebuild}
            onRemoved={() => {
              setOpen(null);
              setCurrent(null);
            }}
          />
        </Stack>
        <CurriculumChat
          scopes={scopes}
          onPick={pickScope}
          onOpenLesson={openLesson}
          onBuild={(verify) => {
            withIds(scopeIds(ai.scope), buildAll(verify));
          }}
          onVerify={() => {
            withIds(scopeIds(ai.scope), setVerifying);
          }}
        />
      </Stack>
    </Stack>
  );
}

/** Says whether the selection is this page or every match, and offers the other. */
function SelectionBar({
  selection,
  total,
  onPage,
  wholePage,
  onChoose,
  actions,
}: {
  selection: Selection;
  total: number;
  onPage: number;
  wholePage: boolean;
  onChoose: (next: Selection) => void;
  actions: ReactNode;
}) {
  const count = countOf(selection, total);
  if (count === 0 && !selection.all) return null;

  const clear = (
    <Button
      size="small"
      onClick={() => {
        onChoose(NONE);
      }}
    >
      Clear selection
    </Button>
  );

  if (selection.all) {
    return (
      <Stack
        direction="row"
        spacing={1}
        sx={{ alignItems: 'center' }}
        role="status"
      >
        <Typography variant="body2">
          {selection.except.size === 0
            ? `All ${counted(count, 'lesson')} matching the filters are selected, on every page.`
            : `${count.toLocaleString()} of the ${counted(total, 'matching lesson')} are selected, on every page.`}
        </Typography>
        {clear}
        {actions}
      </Stack>
    );
  }

  return (
    <Stack
      direction="row"
      spacing={1}
      sx={{ alignItems: 'center' }}
      role="status"
    >
      <Typography variant="body2">
        {wholePage && count === onPage
          ? `All ${counted(count, 'lesson')} on this page are selected.`
          : `${counted(count, 'lesson')} selected.`}
      </Typography>
      {total > count && (
        <Button
          size="small"
          onClick={() => {
            onChoose(ALL);
          }}
        >
          Select all {counted(total, 'matching lesson')}
        </Button>
      )}
      {clear}
      {actions}
    </Stack>
  );
}

/**
 * A running rebuild reads its job, so the row says QA once the rebuilt lesson is under
 * review. Polled here too: after a reload no progress bar asks for a job in review.
 */
function RowStatus({ row }: { row: PlannedLesson }) {
  const job = useQuery({
    ...jobQuery(row.buildJobId ?? ''),
    enabled: row.rebuilding && row.buildJobId !== null,
    refetchInterval: POLL_MS,
  });
  const label =
    !row.rebuilding || job.data === undefined
      ? statusLabel(row)
      : REBUILD_LABEL[rebuildPhase(job.data)];
  if (!row.storyChanged) return <>{label}</>;
  return (
    <Stack spacing={0.5} sx={{ alignItems: 'flex-start' }}>
      <span>{label}</span>
      <Chip size="small" color="warning" label={STORY_CHANGED} />
    </Stack>
  );
}

/** A long cell cut to one line, the start of it on hover. */
function Cut({
  text,
  width,
  action,
}: {
  text: string;
  width: number;
  action?: { label: string; onClick: () => void };
}) {
  const shown = text === '' ? '—' : text;
  return (
    <TableCell>
      <Tooltip describeChild title={text.slice(0, PREVIEW_CHARS)}>
        {action === undefined ? (
          <Typography variant="body2" noWrap sx={{ maxWidth: width }}>
            {shown}
          </Typography>
        ) : (
          <Link
            component="button"
            variant="body2"
            noWrap
            aria-label={action.label}
            sx={{ maxWidth: width, display: 'block', textAlign: 'left' }}
            onClick={action.onClick}
          >
            {shown}
          </Link>
        )}
      </Tooltip>
    </TableCell>
  );
}

function Sources({ row }: { row: PlannedLesson }) {
  const sources = [
    { name: row.primarySourceName, url: row.primarySourceUrl },
    { name: row.additionalSourceName, url: row.additionalSourceUrl },
  ].filter((one) => one.name !== '' || one.url !== '');
  if (sources.length === 0) return <>—</>;
  return (
    <Stack>
      {sources.map((one) =>
        one.url === '' ? (
          <Typography
            key={one.name}
            variant="body2"
            noWrap
            sx={{ maxWidth: NAME_WIDTH }}
          >
            {one.name}
          </Typography>
        ) : (
          <Link
            key={one.url}
            href={one.url}
            target="_blank"
            rel="noreferrer"
            variant="body2"
            noWrap
            sx={{ maxWidth: NAME_WIDTH, display: 'block' }}
          >
            {one.name === '' ? one.url : one.name}
          </Link>
        ),
      )}
    </Stack>
  );
}
